<template>
  <FormCard
    :title="$t('incidentDetails.title')"
    :subtitle="$t('incidentDetails.subtitle')"
    icon
    iconBgClass="bg-gradient-to-br from-red-500 to-pink-600"
  >
    <template #icon>
      <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </template>

    <form @submit.prevent="handleSubmit" class="space-y-6" novalidate>
      <!-- Incident Description -->
      <div class="form-group">
        <label for="incident_description" class="form-label">
          <div class="flex items-center gap-2 mb-2">
            <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-900">{{ $t('incidentDetails.descriptionTitle') }} <span class="text-red-500">*</span></h3>
              <p class="text-sm text-gray-600">{{ $t('incidentDetails.descriptionHint') }}</p>
            </div>
          </div>
        </label>
        <textarea
          id="incident_description"
          :value="form.incident_description"
          @input="updateField('incident_description', $event.target.value)"
          @blur="validateField('incident_description')"
          maxlength="1240"
          :placeholder="$t('incidentDetails.descriptionPlaceholder')"
          class="form-textarea"
          :class="{ 'error': localErrors.incident_description || errors.incident_description }"
          required
          rows="6"
        ></textarea>
        <div class="flex justify-between items-center mt-2">
          <div v-if="localErrors.incident_description || errors.incident_description" class="form-error">
            {{ localErrors.incident_description || errors.incident_description }}
          </div>
          <div 
            class="text-xs font-medium ml-auto"
            :class="form.incident_description && form.incident_description.length > 1200 ? 'text-yellow-600' : 'text-gray-500'"
          >
            {{ form.incident_description ? form.incident_description.length : 0 }}/1240
          </div>
        </div>
      </div>

      <!-- Incident Date -->
      <div class="form-group">
        <label for="incident_date" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <span>{{ $t('incidentDetails.dateLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        <input
          type="date"
          id="incident_date"
          :value="form.incident_date"
          @change="updateField('incident_date', $event.target.value)"
          @blur="validateField('incident_date')"
          :max="currentDate"
          class="form-input"
          :class="{ 'error': localErrors.incident_date || errors.incident_date }"
          required
        />
        <div v-if="localErrors.incident_date || errors.incident_date" class="form-error">
          {{ localErrors.incident_date || errors.incident_date }}
        </div>
      </div>

      <!-- Incident Type Selection -->
      <div class="form-group">
        <label class="form-label">
          <div class="flex items-center gap-2 mb-3">
            <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <span>{{ $t('incidentDetails.typeLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        
        <div class="incident-types-grid">
          <label 
            v-for="(incident, index) in incidents" 
            :key="index" 
            class="incident-checkbox"
            :class="{ 'selected': isSelected(incident) }"
          >
            <input
              type="checkbox"
              :checked="isSelected(incident)"
              @change="toggleIncident(incident)"
              class="sr-only"
            />
            <span class="checkbox-indicator">
              <svg v-if="isSelected(incident)" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </span>
            <span class="checkbox-text">{{ incident }}</span>
          </label>
        </div>
        
        <!-- Other Incident Input -->
        <div v-if="isOtherSelected" class="mt-4">
          <input
            type="text"
            :value="form.otherIncident"
            @input="updateField('otherIncident', $event.target.value)"
            @blur="validateField('otherIncident')"
            :placeholder="$t('incidentDetails.otherPlaceholder')"
            class="form-input"
            :class="{ 'error': localErrors.otherIncident || errors.otherIncident }"
            maxlength="100"
            required
          />
          <p class="text-xs text-gray-500 mt-1">{{ $t('incidentDetails.otherPlaceholder') }} <span class="text-red-500">*</span></p>
          <div v-if="localErrors.otherIncident || errors.otherIncident" class="form-error">
            {{ localErrors.otherIncident || errors.otherIncident }}
          </div>
        </div>
        
        <div v-if="localErrors.incident_type || errors.incident_type" class="form-error mt-2">
          {{ localErrors.incident_type || errors.incident_type }}
        </div>
      </div>

      <!-- Smart completeness warning (phone theft / scam) -->
      <div v-if="smartWarnings.length" class="p-4 bg-amber-50 border border-amber-300 rounded-xl">
        <div class="flex items-start gap-2 text-amber-800">
          <svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div class="text-sm">
            <p class="font-semibold mb-1">{{ $t('incidentDetails.smart.title') }}</p>
            <ul class="list-disc ml-4 space-y-0.5">
              <li v-for="(w, i) in smartWarnings" :key="i">{{ w }}</li>
            </ul>
            <p class="mt-2 text-xs text-amber-700">{{ $t('incidentDetails.smart.hint') }}</p>
          </div>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="flex justify-center gap-3 pt-4">
        <button 
          type="button" 
          @click="$emit('back')" 
          class="btn-secondary group"
        >
          <svg class="w-4 h-4 mr-1 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          {{ $t('incidentDetails.backBtn') }}
        </button>
        
        <button 
          type="submit" 
          class="btn-primary group"
          :disabled="loading"
        >
          <svg v-if="loading" class="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <span>{{ loading ? $t('incidentDetails.savingBtn') : (smartWarnings.length && warningAcknowledged === false ? $t('incidentDetails.smart.continueAnyway') : $t('incidentDetails.nextBtn')) }}</span>
          <svg v-if="!loading" class="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </form>
  </FormCard>
</template>

<script>
import FormCard from '../ui/FormCard.vue';

export default {
  name: 'IncidentDetails',
  components: { FormCard },
  props: {
    form: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    incidents: { type: Array, required: true }
  },
  emits: ['submit', 'back', 'update:field', 'toggle-incident'],
  data() {
    return {
      localErrors: {
        incident_description: '',
        incident_date: '',
        incident_type: '',
        otherIncident: ''
      },
      smartWarnings: [],
      warningAcknowledged: false
    };
  },
  computed: {
    currentDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    selectedTypes() {
      return this.form.incident_type
        ? this.form.incident_type.split(',').map(t => t.trim()).filter(Boolean)
        : [];
    },
    isOtherSelected() {
      // Match against the translated "other" label in any language
      const otherLabel = this.$t('incidentForm.crimes.other');
      return this.selectedTypes.includes(otherLabel);
    }
  },
  methods: {
    updateField(field, value) {
      this.$emit('update:field', { field, value });
      if (this.localErrors[field]) {
        this.localErrors[field] = '';
      }
      // If the user edits the description after a warning, re-evaluate on the
      // next submit instead of letting a stale warning stand.
      if (field === 'incident_description' && this.smartWarnings.length) {
        this.smartWarnings = [];
        this.warningAcknowledged = false;
      }
    },
    
    isSelected(incident) {
      return this.selectedTypes.includes(incident);
    },
    
    toggleIncident(incident) {
      this.$emit('toggle-incident', incident);
      this.localErrors.incident_type = '';
    },
    
    validateField(field) {
      const value = this.form[field];
      
      switch (field) {
        case 'incident_description':
          if (!value || !value.trim()) {
            this.localErrors.incident_description = this.$t('incidentDetails.errors.descriptionRequired');
            return false;
          }
          if (value.trim().length < 20) {
            this.localErrors.incident_description = this.$t('incidentDetails.errors.descriptionMin');
            return false;
          }
          this.localErrors.incident_description = '';
          return true;
          
        case 'incident_date':
          if (!value) {
            this.localErrors.incident_date = this.$t('incidentDetails.errors.dateRequired');
            return false;
          }
          this.localErrors.incident_date = '';
          return true;
          
        case 'incident_type':
          if (!this.form.incident_type || this.selectedTypes.length === 0) {
            this.localErrors.incident_type = this.$t('incidentDetails.errors.typeRequired');
            return false;
          }
          this.localErrors.incident_type = '';
          return true;
          
        case 'otherIncident':
          if (this.isOtherSelected && (!value || !value.trim())) {
            this.localErrors.otherIncident = this.$t('incidentDetails.errors.otherRequired');
            return false;
          }
          this.localErrors.otherIncident = '';
          return true;
      }
      
      return true;
    },
    
    validateAll() {
      let isValid = true;
      
      if (!this.validateField('incident_description')) isValid = false;
      if (!this.validateField('incident_date')) isValid = false;
      if (!this.validateField('incident_type')) isValid = false;
      if (this.isOtherSelected && !this.validateField('otherIncident')) isValid = false;
      
      return isValid;
    },
    
    // Rule-based completeness check for specific incident types.
    // Scans the description for the key facts and returns a list of missing
    // items. It WARNS the user but never blocks them (they may genuinely not
    // have the information).
    runSmartChecks() {
      const warnings = [];
      const desc = (this.form.incident_description || '').toLowerCase();
      const selected = this.selectedTypes;

      const phoneTheftLabel = this.$t('incidentForm.crimes.Ptheft');
      const scamLabel = this.$t('incidentForm.crimes.scamming');

      // --- Phone theft: IMEI, date stolen, SIM re-registration date ---
      if (selected.includes(phoneTheftLabel)) {
        // IMEI = 15 consecutive digits (allow spaces/dashes between groups).
        const hasImei = /(?:\D|^)(\d[\s-]?){15}(?:\D|$)/.test(this.form.incident_description || '')
          || /imei/i.test(desc);
        if (!hasImei) warnings.push(this.$t('incidentDetails.smart.missingImei'));

        // Any date-like token, or a date keyword.
        const hasDate = /\b\d{1,4}[/.-]\d{1,2}[/.-]\d{1,4}\b/.test(desc)
          || /\b(january|february|march|april|may|june|july|august|september|october|november|december|janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre)\b/.test(desc)
          || this.form.incident_date;
        if (!hasDate) warnings.push(this.$t('incidentDetails.smart.missingTheftDate'));

        // SIM re-registration mention.
        const hasSimReg = /(sim|puce).{0,30}(re[- ]?reg|r[ée]-?enregistr|re[- ]?activ|re[- ]?register|swap|r[ée]activ)/i.test(desc)
          || /(re[- ]?enregistr|r[ée]-?enregistrement|sim swap)/i.test(desc);
        if (!hasSimReg) warnings.push(this.$t('incidentDetails.smart.missingSimReg'));
      }

      // --- Scam: the scammer's channel of communication ---
      if (selected.includes(scamLabel)) {
        const hasChannel = /(whatsapp|telegram|messenger|facebook|instagram|e[- ]?mail|email|gmail|sms|phone|t[ée]l[ée]phone|appel|call|tiktok|snapchat|signal|viber)/i.test(desc);
        if (!hasChannel) warnings.push(this.$t('incidentDetails.smart.missingScamChannel'));
      }

      return warnings;
    },

    handleSubmit() {
      if (!this.validateAll()) return;

      // Run the smart completeness check. If something is missing and the
      // user hasn't acknowledged yet, show the warning and pause once.
      const warnings = this.runSmartChecks();
      if (warnings.length && !this.warningAcknowledged) {
        this.smartWarnings = warnings;
        this.warningAcknowledged = true;
        return; // first press: show warning, don't advance
      }

      // Either nothing missing, or the user chose to continue anyway.
      this.$emit('submit');
    }
  }
}
</script>

<style scoped>
.form-group {
  @apply w-full space-y-2;
}

.form-label {
  @apply block text-sm font-semibold text-gray-700;
}

.form-input {
  @apply w-full px-4 py-3 bg-white text-gray-900 rounded-xl border border-gray-300 
         focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300
         placeholder-gray-500 hover:border-gray-400 shadow-sm;
  font-size: 16px;
}

.form-textarea {
  @apply w-full px-4 py-3 bg-white text-gray-900 rounded-xl border border-gray-300 
         focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300
         placeholder-gray-500 hover:border-gray-400 shadow-sm resize-y min-h-[150px];
  font-size: 16px;
}

.form-input.error,
.form-textarea.error {
  @apply border-red-500 focus:ring-red-500 focus:border-red-500;
}

.form-error {
  @apply text-red-600 text-sm flex items-center;
}

.form-error::before {
  content: '';
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 4px;
  flex-shrink: 0;
  background-image: url("data:image/svg+xml,%3Csvg fill='none' stroke='%23dc2626' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'%3E%3C/path%3E%3C/svg%3E");
}

.incident-types-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1 custom-scrollbar;
}

.incident-checkbox {
  @apply flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-gray-200
         cursor-pointer transition-all duration-200 hover:border-blue-300 hover:bg-blue-50/30;
}

.incident-checkbox.selected {
  @apply border-blue-500 bg-blue-50 shadow-md shadow-blue-500/10;
}

.checkbox-indicator {
  @apply w-5 h-5 rounded-md border-2 border-gray-300 flex items-center justify-center 
         transition-all duration-200 flex-shrink-0;
}

.incident-checkbox.selected .checkbox-indicator {
  @apply bg-gradient-to-br from-blue-500 to-indigo-600 border-blue-500;
}

.checkbox-text {
  @apply text-sm text-gray-700 transition-colors;
}

.incident-checkbox.selected .checkbox-text {
  @apply text-blue-700 font-medium;
}

.btn-primary {
  @apply bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700
         text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg
         transition-all duration-300 flex items-center justify-center disabled:opacity-50
         disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98];
  min-height: 44px;
}

.btn-secondary {
  @apply bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-5 rounded-xl
         shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center
         border border-gray-300 transform hover:scale-[1.02] active:scale-[0.98];
  min-height: 44px;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  @apply bg-gray-100 rounded-full;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  @apply bg-gray-300 rounded-full hover:bg-gray-400;
}
</style>